import { Link } from 'react-router-dom'
import { Receipt } from 'lucide-react'
import { useInvoices } from '@/lib/queries.js'
import { money, formatDate } from '@/lib/format.js'
import { Card, PageHeader } from '@/components/ui/card.jsx'
import { Alert, EmptyState, Spinner } from '@/components/ui/feedback.jsx'
import { StatusBadge } from '@/components/ui/badge.jsx'
import { humanMessage } from '@/lib/errors.js'

/**
 * Invoice list.
 *
 * Read-only: invoices are created FROM an accepted estimate on the job screen,
 * never from here. That is not a limitation to work around — it is the loop.
 * An invoice with no job and no accepted quote behind it is the shape of
 * bookkeeping, not of contracting work, and the empty state says where to go
 * instead of offering a "new invoice" button that would dead-end.
 */
export function InvoiceList () {
  const { data: invoices = [], isPending, error } = useInvoices()

  // Phase 2: `refunded` joins the settled set, and a NEGATIVE balance is
  // clamped out of the total. An overpaid invoice (D25.4) genuinely carries a
  // negative balance, and letting it net off against real receivables would
  // understate what the contractor is owed across the whole list — which is
  // the one number on this screen anybody reads.
  const SETTLED = ['paid', 'voided', 'written_off', 'refunded']
  const outstanding = invoices
    .filter(i => !SETTLED.includes(i.status))
    .reduce((sum, i) => sum + Math.max(0, Number(i.balance ?? 0)), 0)

  return (
    <>
      <PageHeader
        title="Invoices"
        description={
          isPending
            ? 'Loading…'
            : invoices.length === 0
              ? 'Nothing raised yet'
              : `${money(outstanding)} outstanding across ${invoices.length} ${invoices.length === 1 ? 'invoice' : 'invoices'}`
        }
      />

      {error && <Alert tone="danger" className="mb-4">{humanMessage(error)}</Alert>}

      {isPending
        ? <div className="flex justify-center py-16"><Spinner label="Loading invoices" /></div>
        : invoices.length === 0
          ? (
            <EmptyState
              icon={Receipt}
              title="No invoices yet"
              description="Invoices are raised from an accepted estimate. Open the job, accept its estimate, and the invoice follows from there."
              action={{ label: 'Go to the pipeline', href: '/' }}
            />
            )
          : (
            <Card className="overflow-hidden">
              <ul className="divide-y divide-line">
                {invoices.map(invoice => (
                  <li key={invoice.id}>
                    <Link
                      to={`/invoices/${invoice.id}`}
                      className="flex min-h-tap items-center gap-3 px-4 py-3 hover:bg-surface-sunken"
                      data-testid="invoice-row"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="tabular block truncate font-mono text-xs text-ink-muted">
                          {invoice.invoice_number}
                        </span>
                        <span className="block text-sm text-ink-subtle">
                          {invoice.due_date ? `Due ${formatDate(invoice.due_date)}` : formatDate(invoice.created_at)}
                        </span>
                      </span>
                      <StatusBadge status={invoice.status} />
                      <span className="tabular w-24 text-right text-sm font-medium text-ink">
                        {money(invoice.total)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
            )}
    </>
  )
}
